import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { initialStateSchedules } from "../defaultState";
import { changeScheduleOrder, deleteSchedule, getSchedules, saveSchedule } from "../../aplApi/lineWoSchedulesHelper";
import { LineWoSchedule } from "../../types/lineWoSchedule";
import { ReduxStateSchedules } from "../../types/redux/reduxStateSchedules";
import { getWorkorderItemsByLineId } from "../../aplApi/workorderItemsHelper";
import moment from "moment";

export const getSchedulesRedux: any = createAsyncThunk("schedules/getSchedulesRedux", async (lineId: number, thunkApi) => {
    try {
        let response = await getSchedules(lineId);
        return response.data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(error.response.data);
    }
});

export const saveScheduleRedux: any = createAsyncThunk("schedules/saveScheduleRedux", async (schedule: LineWoSchedule, thunkApi) => {
    try {
        let lineId = schedule.lineId;
        await saveSchedule(schedule);
        let response = await getSchedules(lineId);
        return response.data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(error.response.data);
    }
});

export const changeScheduleOrderRedux: any = createAsyncThunk(
    "schedules/changeScheduleOrder",
    async (data: { schedule: LineWoSchedule; shift: number }, thunkApi) => {
        try {
            await changeScheduleOrder(data.schedule, data.shift);
            let response = await getSchedules(data.schedule.lineId);
            return response.data;
        } catch (error: any) {
            return thunkApi.rejectWithValue(error.response.data);
        }
    }
);

export const deleteScheduleRedux: any = createAsyncThunk("schedules/deleteScheduleRedux", async (schedule: LineWoSchedule, thunkApi) => {
    try {
        await deleteSchedule(schedule.scheduleId);
        let response = await getSchedules(schedule.lineId);
        return response.data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(error.response.data);
    }
});

export const getWorkordersRedux: any = createAsyncThunk("schedules/getWorkordersRedux", async (lineId: number, thunkApi) => {
    try {
        let response = await getWorkorderItemsByLineId(lineId);
        return response.data;
    } catch (error: any) {
        return thunkApi.rejectWithValue(error.response.data);
    }
});

const schedulesFeature: any = createSlice({
    name: "schedules",
    initialState: initialStateSchedules,
    reducers: {
        clearSchedulesRedux: function (state) {
            return initialStateSchedules;
        },
        setSelectedScheduleRedux: function (state, action) {
            return {
                ...state,
                selectedSchedule: action.payload
            };
        },
        setSelectedWorkorderRedux: function (state, action) {
            return {
                ...state,
                selectedWorkorder: action.payload
            };
        },
        setSelectedScheduleStartDateRedux: function (state, action) {
            return {
                ...state,
                selectedSchedule:
                    state.selectedSchedule.length === 1
                        ? [
                              {
                                  ...state.selectedSchedule[0],
                                  startDateTime: action.payload ? moment(action.payload).toObject() : null
                              }
                          ]
                        : []
            } as ReduxStateSchedules;
        },
        setSelectedScheduleEndDateRedux: function (state, action) {
            return {
                ...state,
                selectedSchedule:
                    state.selectedSchedule.length === 1
                        ? [
                              {
                                  ...state.selectedSchedule[0],
                                  endDateTime: action.payload ? moment(action.payload).toObject() : null
                              }
                          ]
                        : []
            } as ReduxStateSchedules;
        }
    },

    extraReducers: builder => {
        builder
            .addCase(getSchedulesRedux.pending, state => {
                return {
                    ...state,
                    schedulesLoading: true
                } as ReduxStateSchedules;
            })
            .addCase(getSchedulesRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    schedules: action.payload,
                    schedulesErrorMessage: "",
                    schedulesLoading: false
                } as ReduxStateSchedules;
            })
            .addCase(getSchedulesRedux.rejected, (state, action) => {
                return {
                    ...state,
                    schedulesLoading: false,
                    schedules: [],
                    schedulesErrorMessage: action.error.message ?? "API error"
                } as ReduxStateSchedules;
            });

        builder
            .addCase(saveScheduleRedux.pending, state => {
                return {
                    ...state,
                    schedulesLoading: true
                } as ReduxStateSchedules;
            })
            .addCase(saveScheduleRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    schedules: action.payload,
                    schedulesErrorMessage: "",
                    schedulesLoading: false
                } as ReduxStateSchedules;
            })
            .addCase(saveScheduleRedux.rejected, (state, action) => {
                return {
                    ...state,
                    schedulesLoading: false,
                    schedules: [],
                    schedulesErrorMessage: action.error.message ?? "API error"
                } as ReduxStateSchedules;
            });

        builder
            .addCase(changeScheduleOrderRedux.pending, state => {
                return {
                    ...state,
                    schedulesLoading: true
                } as ReduxStateSchedules;
            })
            .addCase(changeScheduleOrderRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    schedules: action.payload,
                    schedulesErrorMessage: "",
                    schedulesLoading: false
                } as ReduxStateSchedules;
            })
            .addCase(changeScheduleOrderRedux.rejected, (state, action) => {
                return {
                    ...state,
                    schedulesLoading: false,
                    schedulesErrorMessage: action.error.message ?? "API error"
                } as ReduxStateSchedules;
            });

        builder
            .addCase(deleteScheduleRedux.pending, state => {
                return {
                    ...state,
                    schedulesLoading: true
                } as ReduxStateSchedules;
            })
            .addCase(deleteScheduleRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    schedules: action.payload,
                    schedulesErrorMessage: "",
                    schedulesLoading: false,
                    selectedSchedule: []
                } as ReduxStateSchedules;
            })
            .addCase(deleteScheduleRedux.rejected, (state, action) => {
                return {
                    ...state,
                    schedulesLoading: false,
                    schedulesErrorMessage: action.error.message ?? "API error"
                } as ReduxStateSchedules;
            });

        builder
            .addCase(getWorkordersRedux.pending, state => {
                return {
                    ...state,
                    workordersLoading: true
                } as ReduxStateSchedules;
            })
            .addCase(getWorkordersRedux.fulfilled, (state, action) => {
                return {
                    ...state,
                    workorders: action.payload,
                    workordersErrorMessage: "",
                    workordersLoading: false
                } as ReduxStateSchedules;
            })
            .addCase(getWorkordersRedux.rejected, (state, action) => {
                return {
                    ...state,
                    workordersLoading: false,
                    workorders: [],
                    workordersErrorMessage: action.error.message ?? "API error"
                } as ReduxStateSchedules;
            });
    }
});

export const {
    clearSchedulesRedux,
    setSelectedScheduleRedux,
    setSelectedWorkorderRedux,
    setSelectedScheduleStartDateRedux,
    setSelectedScheduleEndDateRedux
} = schedulesFeature.actions;
export default schedulesFeature.reducer;
